import React, { useEffect, useState, useCallback } from 'react'
import useDrivePicker from 'react-google-drive-picker'

/**
 * FilePickerPopup - A standalone page component for Google Drive file picker
 *
 * This component is designed to run in a popup window and communicate with the
 * parent window via postMessage. It's used when the main app is running in an
 * iframe (e.g., BigCommerce) where the Google Picker popup doesn't work correctly.
 */
function FilePickerPopup() {
  const [openPicker, authResponse] = useDrivePicker()
  const [status, setStatus] = useState('waiting')
  const [error, setError] = useState<string | null>(null)
  const [config, setConfig] = useState<any>(null)

  // Handle messages from parent window
  const handleMessage = useCallback((event: MessageEvent) => {
    // Only accept messages from same origin (security)
    if (event.origin !== window.location.origin) {
      return
    }

    const { type, config: pickerConfig } = event.data || {}

    if (type === 'GOOGLE_PICKER_CONFIG' && pickerConfig) {
      setConfig(pickerConfig)
      setStatus('opening')
    }
  }, [])

  // Set up message listener
  useEffect(() => {
    window.addEventListener('message', handleMessage)

    // Notify parent that we're ready
    if (window.opener) {
      window.opener.postMessage({ type: 'GOOGLE_PICKER_READY' }, window.location.origin)
    }

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [handleMessage])

  // Open picker when config is received
  useEffect(() => {
    if (config && status === 'opening') {
      try {
        openPicker({
          clientId: config.clientId,
          developerKey: config.developerKey,
          appId: config.appId,
          viewId: config.viewId,
          token: config.token,
          customScopes: config.customScopes,
          showUploadView: config.showUploadView,
          showUploadFolders: config.showUploadFolders,
          setSelectFolderEnabled: config.setSelectFolderEnabled,
          supportDrives: config.supportDrives,
          multiselect: config.multiselect,
          callbackFunction: (data: any) => {
            console.log('Google Picker callback:', data.action, data)

            // The callback fires with different actions:
            // - 'loaded': picker has loaded (do nothing, wait for user)
            // - 'picked': user selected something
            // - 'cancel': user cancelled/closed the picker

            if (data.action === 'loaded') {
              // Picker is ready, do nothing - wait for user interaction
              setStatus('picker_open')
              return
            }

            if (window.opener) {
              if (data.action === 'cancel') {
                window.opener.postMessage({ type: 'GOOGLE_PICKER_CANCEL' }, window.location.origin)
                setStatus('cancelled')
                // Auto-close after a short delay
                setTimeout(() => {
                  window.close()
                }, 500)
              } else if (data.action === 'picked' && data.docs?.length > 0) {
                // authResponse is the OAuth response the picker actually used
                // — it reflects in-picker account switches. config.token is
                // only the seed we passed in. Prefer authResponse; fall back
                // to config.token only as a last resort.
                const pickerToken = (authResponse && (authResponse as any).access_token) || config.token
                window.opener.postMessage({
                  type: 'GOOGLE_PICKER_SELECTION',
                  data: {
                    action: 'picked',
                    docs: data.docs,
                    accessToken: pickerToken,
                  }
                }, window.location.origin)
                setStatus('selected')
                // Auto-close after a short delay
                setTimeout(() => {
                  window.close()
                }, 500)
              }
            }
          },
        })
        setStatus('picker_open')
      } catch (err: any) {
        console.error('Error opening picker:', err)
        setError(err.message)
        setStatus('error')
      }
    }
  }, [config, status, openPicker])

  // Handle manual close
  const handleCancel = () => {
    if (window.opener) {
      window.opener.postMessage({ type: 'GOOGLE_PICKER_CANCEL' }, window.location.origin)
    }
    window.close()
  }

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <div style={styles.header}>
          <img
            src="/assets/images/logos/googledrive.svg"
            alt="Google Drive"
            style={styles.logo}
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
          />
          <h2 style={styles.title}>Google Drive File Picker</h2>
        </div>

        {status === 'waiting' && (
          <div style={styles.statusContainer}>
            <div style={styles.spinner}></div>
            <p style={styles.statusText}>Connecting to parent window...</p>
          </div>
        )}

        {status === 'opening' && (
          <div style={styles.statusContainer}>
            <div style={styles.spinner}></div>
            <p style={styles.statusText}>Opening Google Drive picker...</p>
          </div>
        )}

        {status === 'picker_open' && (
          <div style={styles.statusContainer}>
            <p style={styles.statusText}>
              Please select a file or folder from the Google Drive picker.
            </p>
            <p style={styles.hintText}>
              If the picker doesn't appear, please check if popups are blocked.
            </p>
          </div>
        )}

        {status === 'selected' && (
          <div style={styles.statusContainer}>
            <span style={styles.checkmark}>&#10003;</span>
            <p style={styles.statusText}>Selection complete! This window will close automatically.</p>
          </div>
        )}

        {status === 'cancelled' && (
          <div style={styles.statusContainer}>
            <p style={styles.statusText}>Selection cancelled. This window will close automatically.</p>
          </div>
        )}

        {status === 'error' && (
          <div style={styles.statusContainer}>
            <p style={styles.errorText}>Error: {error}</p>
          </div>
        )}

        <button onClick={handleCancel} style={styles.cancelButton}>
          Cancel &amp; Close
        </button>
      </div>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, sans-serif',
    padding: '20px',
  },
  content: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '40px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    textAlign: 'center',
    maxWidth: '500px',
    width: '100%',
  },
  header: {
    marginBottom: '30px',
  },
  logo: {
    width: '60px',
    height: '60px',
    marginBottom: '15px',
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#333',
    margin: '0',
  },
  statusContainer: {
    marginBottom: '30px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #e0e0e0',
    borderTopColor: '#4285f4',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 20px',
  },
  statusText: {
    fontSize: '16px',
    color: '#666',
    margin: '0',
  },
  hintText: {
    fontSize: '14px',
    color: '#999',
    marginTop: '10px',
  },
  errorText: {
    fontSize: '16px',
    color: '#d32f2f',
    margin: '0',
  },
  checkmark: {
    display: 'inline-block',
    fontSize: '48px',
    color: '#4caf50',
    marginBottom: '15px',
  },
  cancelButton: {
    backgroundColor: '#f5f5f5',
    border: '1px solid #ddd',
    borderRadius: '6px',
    padding: '12px 24px',
    fontSize: '14px',
    color: '#666',
    cursor: 'pointer',
  },
}

export default FilePickerPopup
